import React from 'react';
import { Typography, IconButton, Divider, Collapse, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { formatCurrency } from '@acorn-portal/shared/src/utils/formatter';
import { IPaymentOptionValue } from '@acorn-portal/shared/src/components/renewal/paymentOptionsSelector';
import {
	FinancePlan,
	FinancePlanWithDeposit,
	RenewalRatingResponse,
} from '@acorn-portal/shared/src/services/renewalsApi';

export type MonthlyValues = {
	rates: FinancePlanWithDeposit | FinancePlan;
	annualPremium: number;
	insuranceTaxPercentage: number;
	policyAdministrationFee: number;
	depositValue?: string;
};

interface PriceDetailsProps {
	expanded: boolean;
	setExpanded: (expanded: boolean) => void;
	paymentOptions: RenewalRatingResponse;
	value?: IPaymentOptionValue;
	monthlyValues?: MonthlyValues;
}

interface CollapseDetailsProps {
	expanded: boolean;
	monthlyValues: MonthlyValues;
	setExpanded: (expanded: boolean) => void;
	deposit: boolean;
}
const footerDetailStyle = {
	display: 'flex',
	justifyContent: 'space-between',
	color: 'white',
};

const CollapseDetails: React.FC<CollapseDetailsProps> = ({
	expanded,
	monthlyValues,
	setExpanded,
	deposit,
}: CollapseDetailsProps) => {
	const isDeposit = monthlyValues?.depositValue === 'yes';
	const isUnequalInstallments = monthlyValues?.rates.unequalInstallmentAmount !== 0;

	return (
		<Collapse in={expanded}>
			<Divider sx={{ borderColor: 'white', mb: 2, mt: 1 }} />

			{isUnequalInstallments && isDeposit && (
				<>
					{deposit && (
						<Typography variant='body2' sx={footerDetailStyle}>
							First instalment of{''}
							<span style={{ fontWeight: 'bold' }}>{formatCurrency(monthlyValues.rates.unequalInstallmentAmount)}</span>
						</Typography>
					)}

					<Typography variant='body2' sx={footerDetailStyle}>
						Followed by {monthlyValues.rates.installmentCount! - 1} monthly payments of
						<span style={{ fontWeight: 'bold' }}>{formatCurrency(monthlyValues.rates.standardInstallmentAmount)}</span>
					</Typography>
				</>
			)}

			<Typography variant='body2' sx={footerDetailStyle}>
				Charge for credit{''}
				<span style={{ fontWeight: 'bold' }}>{formatCurrency(monthlyValues.rates.chargeForCredit)}</span>
			</Typography>
			<Typography variant='body2' sx={footerDetailStyle}>
				Annual premium{''}
				<span style={{ fontWeight: 'bold' }}>
					{formatCurrency(monthlyValues.rates.totalPremium - monthlyValues.rates.chargeForCredit)}
				</span>
			</Typography>
			<Typography variant='body2' sx={footerDetailStyle}>
				Overall payable{''}
				<span style={{ fontWeight: 'bold' }}>{formatCurrency(monthlyValues.rates.totalPremium)}</span>
			</Typography>

			{monthlyValues.annualPremium > 0 && (
				<Typography variant='body2' sx={footerDetailStyle}>
					<span style={{ fontWeight: 'bold', textAlign: 'right', flex: 'auto' }}>
						<i>({monthlyValues.rates.apr}% APR)</i>
					</span>
				</Typography>
			)}

			<Button
				variant='text'
				onClick={() => setExpanded(false)}
				sx={{
					width: '100%',
					color: 'white',
					mt: 1,
					':hover': { backgroundColor: 'initial' },
				}}
			>
				Close
			</Button>
		</Collapse>
	);
};

const PriceFooterDetails: React.FC<PriceDetailsProps> = ({
	expanded,
	setExpanded,
	paymentOptions,
	value,
	monthlyValues,
}: PriceDetailsProps) => {
	const isFinance = value?.method !== 'total';
	const hasDeposit = value?.deposit;
	const isUnequalInstallments = monthlyValues?.rates?.unequalInstallmentAmount !== 0;

	// Extracted label logic
	let paymentLabel = '';
	if (hasDeposit) {
		paymentLabel = 'Amount to pay now';
	} else if (isUnequalInstallments) {
		paymentLabel = 'First instalment of';
	} else {
		paymentLabel = 'Monthly payments of';
	}

	return (
		<>
			{value && !isFinance && (
				<Typography variant='h6' align='left' gutterBottom sx={footerDetailStyle}>
					Amount to pay now{''}
					<span style={{ fontWeight: 'bold' }}>{formatCurrency(paymentOptions.annualPremium)}</span>
				</Typography>
			)}
			{value && isFinance && (
				<Typography variant='h6' align='left' gutterBottom sx={footerDetailStyle}>
					{!hasDeposit && !isUnequalInstallments && monthlyValues?.rates.installmentCount} {paymentLabel}
					<span style={{ fontWeight: 'bold' }}>
						{(() => {
							if (value.deposit) {
								return formatCurrency((monthlyValues?.rates as FinancePlanWithDeposit)?.depositAmount ?? 0);
							} else if (isUnequalInstallments) {
								return formatCurrency(monthlyValues?.rates?.unequalInstallmentAmount ?? 0);
							} else {
								return formatCurrency(monthlyValues?.rates?.standardInstallmentAmount ?? 0);
							}
						})()}
						<IconButton
							edge='end'
							size='medium'
							sx={{ color: 'white', marginLeft: 1 }}
							onClick={() => setExpanded(!expanded)}
						>
							{!expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
						</IconButton>
					</span>
				</Typography>
			)}
			{value && isFinance && (
				<>
					{monthlyValues && (
						<CollapseDetails
							expanded={expanded}
							monthlyValues={monthlyValues}
							deposit={hasDeposit ?? false}
							setExpanded={setExpanded}
						/>
					)}
				</>
			)}
		</>
	);
};

export { PriceFooterDetails, CollapseDetails };
